/* eslint-disable react/no-multi-comp */
/* eslint-disable react/display-name */
import React, { forwardRef, useState } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import { List, ListItem, Button, Collapse, Typography } from '@material-ui/core';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

const useStyles = makeStyles(theme => ({
    root: {
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        padding: '6px 0',
        '&::-webkit-scrollbar': {
            width: 3
        },
        '&::-webkit-scrollbar-thumb': {
            background: '#3F3E58',
            borderRadius: 2
        }
    },

    // ── Nav item ──
    item: {
        display: 'flex',
        paddingTop: 0,
        paddingBottom: 0
    },
    button: {
        color: 'white',
        padding: '8px 14px',
        borderRadius: 0,
        justifyContent: 'flex-start',
        textTransform: 'none',
        fontWeight: 400,
        letterSpacing: 0,
        borderLeft: '3px solid transparent',
        width: '100%',
        marginTop: 7,
        '&:hover': {
            backgroundColor: '#4872867a'
        },
        '& .MuiButton-startIcon': {
            marginRight: 9,
            color: 'white'
        }
    },
    active: {
        color: '#EDEEF5',
        borderLeftColor: '#007AB7',
        backgroundColor: '#487286',
        '& .MuiButton-startIcon': {
            color: 'white'
        }
    },

    // ── Section header ──
    sectionHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 14px 5px',
        marginTop: 4,
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover $sectionTitle': {
            color: '#9B9AB8'
        }
    },
    sectionTitle: {
        fontSize: 12,
        fontWeight: 500,
        letterSpacing: '0.7px',
        color: '#ffffff69',
        transition: 'color 0.12s'
    },
    sectionChevron: {
        color: '#5E5D7A',
        transition: 'transform 0.2s ease',
        flexShrink: 0
    },

    // ── Disconnect item ──
    disconnectButton: {
        color: '#5E5D7A',
        padding: '8px 14px',
        borderRadius: 0,
        justifyContent: 'flex-start',
        textTransform: 'none',
        fontWeight: 400,
        borderLeft: '3px solid transparent',
        width: '100%',
        marginTop: 8,
        '&:hover': {
            backgroundColor: '#343252',
            color: '#E85D24'
        },
        '& .MuiButton-startIcon': {
            marginRight: 9,
            '& svg': {
                fontSize: 15
            }
        }
    }
}));

const CustomRouterLink = forwardRef((props, ref) => (
    <div ref={ref} style={{ flexGrow: 1 }}>
        <RouterLink {...props} />
    </div>
));

const SidebarNav = props => {
    const { pages, className, onClose, roleId, defaultPages, disconnected } = props;
    const classes = useStyles();

    const sections = [
        { moduleId: 0, sectionName: 'Module marché' },
        { moduleId: 1, sectionName: 'Module domaine' }
    ];

    const [collapsed, setCollapsed] = useState({ 0: false, 1: false });

    const toggleSection = moduleId => {
        setCollapsed(prev => ({ ...prev, [moduleId]: !prev[moduleId] }));
    };

    return (
        <List className={clsx(classes.root, className)} disablePadding>
            {/* Default pages (Accueil, Reporting, etc.) */}
            {roleId > 0 &&
                defaultPages[roleId - 1].map(page => (
                    <ListItem className={classes.item} disableGutters key={`default-${page.title}`}>
                        <Button
                            startIcon={page.icon}
                            activeClassName={classes.active}
                            onClick={onClose}
                            className={classes.button}
                            component={CustomRouterLink}
                            to={page.href}>
                            {page.title}
                        </Button>
                    </ListItem>
                ))}

            {/* Module sections */}
            {roleId > 0 &&
                sections.map(({ sectionName, moduleId }) => (
                    <li key={`section-${sectionName}`}>
                        {/* Section header — clickable to collapse */}
                        <div
                            className={classes.sectionHeader}
                            onClick={() => toggleSection(moduleId)}>
                            <Typography className={classes.sectionTitle}>{sectionName}</Typography>
                            {collapsed[moduleId] ? (
                                <ExpandMoreIcon className={classes.sectionChevron} />
                            ) : (
                                <ExpandLessIcon className={classes.sectionChevron} />
                            )}
                        </div>

                        {/* Collapsible items */}
                        <Collapse in={!collapsed[moduleId]} timeout={200} unmountOnExit={false}>
                            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                                {pages[moduleId][roleId - 1].map(page => (
                                    <ListItem
                                        className={classes.item}
                                        disableGutters
                                        key={`${page.title}-${moduleId}`}>
                                        <Button
                                            startIcon={page.icon}
                                            activeClassName={classes.active}
                                            onClick={onClose}
                                            className={classes.button}
                                            disabled={page.disable}
                                            component={CustomRouterLink}
                                            to={page.href}>
                                            {page.title}
                                        </Button>
                                    </ListItem>
                                ))}
                            </ul>
                        </Collapse>
                    </li>
                ))}

            {/* Disconnected state */}
            {roleId === 0 && (
                <ListItem className={classes.item} disableGutters>
                    <Button
                        startIcon={disconnected.icon}
                        activeClassName={classes.active}
                        onClick={onClose}
                        className={classes.disconnectButton}
                        component={CustomRouterLink}
                        to={disconnected.href}>
                        {disconnected.title}
                    </Button>
                </ListItem>
            )}
        </List>
    );
};

export default SidebarNav;
